import type { Command } from '@/commands/types';
import type { DiffNavigationHandlers } from '@/lib/stores/navigation/diff-navigation-store';
import type { CodeContent } from '@/types/github';
import { ArrowDown, Eye, EyeOff, GitCompare, Maximize2, Minimize2 } from 'lucide-react';

import type { PrV2FileFilterSnapshot } from '../../pr-v2-file-filter-context';
import type { PrV2ComparisonChoice, PrV2VersionComparison } from './pr-v2-comparison-choices';

import { PrV2FileStatusIcon } from '../../shared/pr-v2-file-status-icons';
import { getNextUnviewedFilename } from './pr-v2-active-file-controller';

import { BreadcrumbLabel } from '@/commands/command-item-renderer';
import { prV2Command, prV2ModeRow } from '@/commands/modules/pr-v2-command-group';
import { splitFilePath } from '@/lib/file-paths/split-file-path';

function fileListSentinel(label: string): Command {
  return prV2ModeRow({
    id: 'pr-v2-file-empty-sentinel',
    tag: 'pr-v2-file-empty',
    label,
    action: () => {},
  });
}

/** Lists nothing while the diff loads, and one row saying why when it failed or has no files. */
export function buildPrV2FileRows({
  status,
  emptyMessage,
  groups,
  viewedFiles,
  navigateToFile,
}: {
  status: PrV2FileFilterSnapshot['status'];
  emptyMessage: string;
  groups: ReadonlyArray<{ files: ReadonlyArray<Pick<CodeContent, 'filename' | 'status'>> }>;
  viewedFiles: ReadonlySet<string>;
  navigateToFile: DiffNavigationHandlers['navigateToFile'];
}): Command[] {
  if (status === 'loading') {
    return [];
  }
  if (status === 'error') {
    return [fileListSentinel('Failed to load code changes')];
  }

  // Hidden test files are listed too, as navigating to one reveals it.
  const rows = groups.flatMap((group) =>
    group.files.map((file) => {
      const { basename, directory } = splitFilePath(file.filename);
      return prV2ModeRow({
        id: `pr-v2-file:${file.filename}`,
        tag: 'pr-v2-file',
        label: file.filename,
        displayLabel: directory ? (
          <BreadcrumbLabel location={directory.slice(0, -1)} title={basename} />
        ) : undefined,
        icon: <PrV2FileStatusIcon status={file.status} />,
        checked: viewedFiles.has(file.filename),
        action: () => {
          navigateToFile(file.filename, { expandFile: true });
        },
      });
    }),
  );

  return rows.length > 0 ? rows : [fileListSentinel(emptyMessage)];
}

/** "Next unviewed file" walks every chapter in review order, not only the rendered one. */
export function buildPrV2ViewedCommands({
  isLoading,
  activeFilename,
  firstRenderedFilename,
  reviewOrderFilenames,
  viewedFiles,
  navigateToFile,
  toggleViewedForFile,
  setAllRenderedFilesCollapsed,
}: {
  isLoading: boolean;
  activeFilename: string | null;
  firstRenderedFilename: string | undefined;
  reviewOrderFilenames: readonly string[];
  viewedFiles: ReadonlySet<string>;
  navigateToFile: DiffNavigationHandlers['navigateToFile'];
  toggleViewedForFile: (filename: string, isCurrentlyViewed: boolean) => boolean;
  setAllRenderedFilesCollapsed: (collapsed: boolean) => void;
}): Command[] {
  const viewedTarget = activeFilename ?? firstRenderedFilename;
  if (isLoading || !viewedTarget) {
    return [];
  }

  const nextUnviewedFilename = getNextUnviewedFilename(
    reviewOrderFilenames,
    viewedFiles,
    activeFilename,
  );
  const viewedTargetName = splitFilePath(viewedTarget).basename;
  const isViewedTargetViewed = viewedFiles.has(viewedTarget);

  return [
    ...(nextUnviewedFilename === null
      ? []
      : [
          prV2Command('pr-v2-next-unviewed-file', {
            label: 'Next unviewed file',
            icon: <ArrowDown />,
            keywords: ['viewed', 'review'],
            action: () => {
              navigateToFile(nextUnviewedFilename, { expandFile: true });
            },
          }),
        ]),
    prV2Command('pr-v2-toggle-file-viewed', {
      label: isViewedTargetViewed
        ? `Mark ${viewedTargetName} as unviewed`
        : `Mark ${viewedTargetName} as viewed`,
      icon: isViewedTargetViewed ? <EyeOff /> : <Eye />,
      shortcutId: 'pr-diff-toggle-viewed',
      keywords: ['viewed', 'reviewed'],
      action: () => {
        toggleViewedForFile(viewedTarget, isViewedTargetViewed);
      },
    }),
    prV2Command('pr-v2-collapse-all-files', {
      label: 'Collapse all files',
      icon: <Minimize2 />,
      keywords: ['collapse', 'files', 'diff'],
      action: () => setAllRenderedFilesCollapsed(true),
    }),
    prV2Command('pr-v2-expand-all-files', {
      label: 'Expand all files',
      icon: <Maximize2 />,
      keywords: ['expand', 'files', 'diff'],
      action: () => setAllRenderedFilesCollapsed(false),
    }),
  ];
}

/** `choices` is null while the pull request has no versions to compare. */
export function buildPrV2CompareCommands({
  choices,
  isComparisonLoading,
  setComparison,
  latestPush,
}: {
  choices: readonly PrV2ComparisonChoice[] | null;
  isComparisonLoading: boolean;
  setComparison: (comparison: PrV2VersionComparison) => void;
  latestPush: { label: string; onReviewLatest: () => void } | null;
}): Command[] {
  if (choices === null && latestPush === null) {
    return [];
  }

  return [
    prV2Command('pr-v2-compare', {
      label: 'Compare...',
      icon: <GitCompare />,
      keywords: ['versions', 'pushes'],
      enterMode: 'pr-v2-compare',
      action: () => false,
    }),
    ...(choices ?? []).map((choice) =>
      prV2ModeRow({
        id: `pr-v2-compare:${choice.id}`,
        tag: 'pr-v2-compare',
        label: choice.label,
        displayLabel: choice.rangeLabel ? (
          <>
            {choice.label}
            <span className="ml-2 text-zinc-500 group-data-[selected=true]:text-zinc-300">
              {choice.rangeLabel}
            </span>
          </>
        ) : undefined,
        keywords: ['compare', 'versions'],
        checked: choice.isActive,
        // The picker's shortcuts ignore picks while a comparison loads, too.
        action: () => {
          if (!isComparisonLoading && !choice.isActive) {
            setComparison(choice.comparison);
          }
        },
      }),
    ),
    ...(latestPush === null
      ? []
      : [
          prV2ModeRow({
            id: 'pr-v2-compare:latest-push',
            tag: 'pr-v2-compare',
            label: `Show latest push (${latestPush.label})`,
            keywords: ['compare', 'versions', 'refresh'],
            action: latestPush.onReviewLatest,
          }),
        ]),
  ];
}
